import { HONEYMOON_FUND_TRAVEL_UTM_CAMPAIGN } from '@shared/constants/honeymoonFundSearch';
import type { HoneymoonEntrySource } from './honeymoonLanding.types';

/**
 * Every promo that can send a couple to the honeymoon page, plus `direct` for anyone who arrived
 * without one.
 *
 * Each value is also registered in `INTERNAL_UTM_SOURCES`. That is what keeps it from being read as
 * an acquisition channel and winning over `honeymoon_landing` when the page builds its search link —
 * without it every booking would be credited to the promo rather than the page it came through.
 *
 * Emails are the exception: they are acquisition channels, so they stay out of that list and keep
 * their `utm_source` on the search link.
 *
 * Closed on purpose: `utm_source` is a query param anyone can type, and an unrecognised value must
 * land as `direct` rather than adding a member to the Amplitude dimension this funnel groups by.
 */
export const HONEYMOON_ENTRY_SOURCES = [
  'manage_registry_banner',
  'manage_registry_gift_tile',
  'track_gifts_widget',
  'track_gifts_dialog',
  'cash_funds_dashboard',
  'dashboard_nav',
  'travel_dropdown',
  'honeymoon_guided_moment',
  'honeymoon_fund_add_success',
  'honeymoon_fund_email',
  'direct',
] as const;

/**
 * Narrows whatever arrived in `utm_source` to a source we report, or `direct`.
 *
 * `useQueryParams` hands back `string | string[] | null`, so a repeated param arrives as an array —
 * which is not a source, and must not be stringified into one. A real acquisition source
 * (`google_ads`, an email blast) is not one of ours either, so it also lands as `direct`.
 */
export const toHoneymoonEntrySource = (value: string | ReadonlyArray<string | null> | null | undefined): HoneymoonEntrySource => {
  const isKnown = (candidate: unknown): candidate is HoneymoonEntrySource => HONEYMOON_ENTRY_SOURCES.some(source => source === candidate);
  return isKnown(value) ? value : 'direct';
};

/**
 * The host honeymoon page, with the promo that linked to it.
 *
 * Standard UTM rather than a private `src` param, so the hop shows up in the same attribution the
 * rest of the travel funnel is read through: `utm_medium=web`, the promo as `utm_source`, and the
 * shared honeymoon campaign. The page reads its entry source straight back off `utm_source`.
 *
 * Lives in shared rather than beside the route: the CTAs that point here sit in `@apps/registry` and
 * in shared components, and neither may import `@apps/admin`'s route paths. Keep the path in step
 * with `routePaths.honeymoon`.
 */
export const buildHoneymoonLandingPath = (eventHandle: string, entrySource: HoneymoonEntrySource): string => {
  const params = new URLSearchParams({ utm_medium: 'web', utm_source: entrySource, utm_campaign: HONEYMOON_FUND_TRAVEL_UTM_CAMPAIGN });
  return `/${eventHandle}/edit/honeymoon?${params.toString()}`;
};
